'use client';

import { Heading, LayoutContainer, Tabs } from '@petsmart-ui/sparky';
import { useMemo } from 'react';

import { trackTabClick } from '@libs/analytics';

import { useProduct } from '@/entities/product';

import { getProductDescriptionMap } from './lib';
import { ProductDescriptionTabHtml } from './ui';
import './ProductDescription.scss';

export const ProductDescription = () => {
    const { product, base } = useProduct();
    const id = parseInt(product?.sku ?? '', 10);
    const sizeChartImgUrl = base?.sizeChartImgUrl;

    const productDescriptionMap = getProductDescriptionMap(base?.variants);
    const productDescription = productDescriptionMap.get(id);

    // Create a stable key based on tab names to prevent re-rendering when the product changes
    const tabsKey = useMemo(() => {
        if (!productDescription) return '';
        return Object.keys(productDescription)
            .sort((a, b) => a.localeCompare(b))
            .join('|');
    }, [productDescription]);

    if (!productDescription) {
        return null;
    }

    const getTabSection = (key: string) => productDescription?.[key as keyof typeof productDescription];

    const getTabKey = (index: number) => {
        if (productDescription) return Object.keys(productDescription)[index];
    };

    const onTabChange = (index: number) => {
        trackTabClick(getTabKey(index));
    };

    return (
        <LayoutContainer id="product-description" className="product-description" data-testid="product-description">
            <Heading tagName="h2" size="title">
                About this item
            </Heading>
            <Tabs key={tabsKey} onChange={onTabChange}>
                {Object.keys(productDescription).map(tabKey => (
                    <Tabs.Panel
                        key={tabKey}
                        title={tabKey}
                        className="product-description-tab"
                        data-testid="product-description-tab"
                    >
                        <ProductDescriptionTabHtml
                            htmlString={getTabSection(tabKey)}
                            sizeChartImgUrl={sizeChartImgUrl}
                            title={tabKey}
                        />
                    </Tabs.Panel>
                ))}
            </Tabs>
        </LayoutContainer>
    );
};
